/**
 * 用户反馈模块 - 英文翻译
 */
export const en = {
  // 模块标题
  title: 'Feedback',
  subtitle: 'Submit feedback and suggestions',
  myFeedback: 'My Feedback',
  feedbackManagement: 'Feedback Management',
  
  // Overview 页面
  overview: {
    quickActions: 'Quick Actions',
    submitFeedback: 'Submit Feedback',
    submitFeedbackDesc: 'Report issues or provide suggestions to help us improve',
    myFeedbackDesc: 'View all {count} feedback records',
    statsDesc: 'View feedback trends and response efficiency',
    recentFeedback: 'Recent Feedback',
    viewAll: 'View All',
    noFeedback: 'No Feedback',
    noFeedbackDesc: 'You haven\'t submitted any feedback yet',
    learnMore: 'Learn More',
  },

  // 反馈类型
  typeBug: 'Bug Report',
  typeFeature: 'Feature Request',
  typeImprovement: 'Improvement',
  typeOther: 'Other',

  // 反馈状态
  statusPending: 'Pending',
  statusInProgress: 'In Progress',
  statusResolved: 'Resolved',
  statusClosed: 'Closed',

  // 优先级
  priorityLow: 'Low',
  priorityMedium: 'Medium',
  priorityHigh: 'High',
  priorityUrgent: 'Urgent',
  priorityUnset: 'Unset',

  // 表单
  form: {
    title: 'Submit Feedback',
    editTitle: 'Edit Feedback',
    type: 'Feedback Type',
    typePlaceholder: 'Select feedback type',
    feedbackTitle: 'Title',
    feedbackTitlePlaceholder: 'Briefly describe your feedback',
    content: 'Description',
    contentPlaceholder: 'Please describe your issue or suggestion in detail...',
    attachments: 'Attachment URLs',
    attachmentsPlaceholder: 'https://example.com/screenshot.png',
    attachmentsHint: 'Up to 5 image URLs',
    dragOrClick: 'Click or drag to upload',
    submit: 'Submit Feedback',
    submitting: 'Submitting...',
    cancel: 'Cancel',
    success: 'Feedback Submitted',
    successDesc: 'Thank you for your feedback! We will process it as soon as possible.',
  },

  // 列表
  list: {
    empty: 'No Feedback',
    emptyDesc: "You haven't submitted any feedback yet",
    adminEmptyDesc: 'There is no user feedback currently',
    searchPlaceholder: 'Search title or content...',
    allTypes: 'All Types',
    allStatus: 'All Status',
    allPriority: 'All Priority',
    allRegions: 'All Regions',
    filter: 'Filter',
    reset: 'Reset',
    refresh: 'Refresh',
    batchUpdate: 'Batch Update',
    export: 'Export',
    selected: '{count} selected',
    total: '{total} feedback(s) in total',
    pageInfo: 'Page {page} of {totalPages}, {total} total',
  },

  // 详情
  detail: {
    title: 'Feedback Details',
    backToList: 'Back to List',
    submitter: 'Submitter',
    submitTime: 'Submit Time',
    updateTime: 'Update Time',
    resolveTime: 'Resolve Time',
    pageUrl: 'Page URL',
    browser: 'Browser Info',
    region: 'Region',
    department: 'Department',
    attachments: 'Attachments',
    attachmentsWithCount: 'Attachments ({count})',
    attachmentItem: 'Attachment {index}',
    noAttachments: 'No attachments',
    timeInfo: 'Time Info',
    techInfo: 'Technical Info',
  },

  // 管理操作
  admin: {
    updateStatus: 'Update Status',
    updateStatusSuccess: 'Status updated successfully',
    changeStatus: 'Change Status',
    priority: 'Priority',
    setPriority: 'Set Priority',
    prioritySuccess: 'Priority set successfully',
    adminNote: 'Internal Note',
    adminNotePlaceholder: 'Add internal note (not visible to user)...',
    adminNoteSuccess: 'Note saved successfully',
    noteHint: 'Not visible to user',
    adminReply: 'Reply to User',
    adminReplyPlaceholder: 'Add reply (visible to user)...',
    adminReplySuccess: 'Reply saved successfully',
    replyHint: 'Visible to user',
    assignee: 'Assignee',
    assigneePlaceholder: 'Select assignee',
    assigneeSuccess: 'Assigned successfully',
    delete: 'Delete Feedback',
    deleteConfirm: 'Are you sure you want to delete this feedback? The user will no longer be able to see it.',
    deleteSuccess: 'Deleted successfully',
    batchUpdateTitle: 'Batch Update Status',
    batchUpdateStatus: 'Target Status',
    batchUpdateConfirm: 'Are you sure you want to update {count} feedback(s) to "{status}" status?',
    batchUpdateSuccess: 'Batch update completed',
    batchUpdatePartialFail: 'Partial failure: {failed}/{total}',
  },

  // 统计
  stats: {
    title: 'Feedback Statistics',
    total: 'Total Feedback',
    pending: 'Pending',
    inProgress: 'In Progress',
    resolved: 'Resolved',
    closed: 'Closed',
    todayNew: 'Today',
    weekNew: 'This Week',
    monthNew: 'This Month',
    avgResolutionTime: 'Avg Resolution Time',
    hours: 'hours',
    byType: 'By Type',
    byStatus: 'By Status',
    byPriority: 'By Priority',
    byRegion: 'By Region',
    showStats: 'Show Stats',
    hideStats: 'Hide Stats',
  },

  // 表格列
  table: {
    content: 'Content',
  },

  // 悬浮按钮
  fab: {
    tooltip: 'Feedback & Suggestions',
  },

  // 其他
  hasReply: 'Replied',

  // 错误消息
  error: {
    loadFailed: 'Failed to load feedback',
    submitFailed: 'Failed to submit feedback',
    updateFailed: 'Failed to update',
    deleteFailed: 'Failed to delete',
    notFound: 'Feedback not found',
    accessDenied: 'Access denied to this feedback',
    invalidStatusTransition: 'Invalid status transition',
    titleRequired: 'Please enter a title',
    contentRequired: 'Please enter a description',
    typeRequired: 'Please select a feedback type',
    titleTooLong: 'Title cannot exceed 200 characters',
    contentTooLong: 'Content cannot exceed 5000 characters',
    attachmentTooLarge: 'Single attachment cannot exceed 5MB',
    attachmentTypeInvalid: 'Only image formats are supported (PNG/JPG/GIF/WebP)',
    attachmentUrlInvalid: 'Invalid URL format',
    attachmentsExceedLimit: 'Maximum 5 images allowed',
    batchLimitExceeded: 'Batch operation supports up to 100 items',
  },

  // 状态变更规则提示
  statusTransition: {
    hint: 'Status transition rules:',
    pendingTo: 'Pending → In Progress / Closed',
    inProgressTo: 'In Progress → Resolved / Closed / Pending',
    resolvedTo: 'Resolved → Closed / In Progress',
    closedTo: 'Closed → Pending',
  },
};
